import { extendComplianceFilings } from '@commenda/api';
import {
  Button,
  DateField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';

interface IBulkExtendFilingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  complianceItemIds: string[];
  latestCurrentDueDate?: string;
  onSuccess?: () => void | Promise<void>;
}

export const BulkExtendFilingsModal = ({
  isOpen,
  onClose,
  complianceItemIds,
  latestCurrentDueDate,
  onSuccess,
}: IBulkExtendFilingsModalProps) => {
  const { setNotification } = useNotification();

  const selectedCount = complianceItemIds.length;
  const [newDueDate, setNewDueDate] = useState<string | undefined>(undefined);
  const [note, setNote] = useState('');

  const isSubmitting = useToggle(false);

  useEffect(() => {
    if (isOpen) {
      setNewDueDate(undefined);
      setNote('');
    }
  }, [isOpen]);

  // The new due date must move every filing's deadline forward
  const isDateInvalid = (() => {
    if (!newDueDate) return true;
    if (!latestCurrentDueDate) return false;
    return NonTimezoneSensitiveDate.fromISO8601String(newDueDate).lte(
      NonTimezoneSensitiveDate.fromISO8601String(latestCurrentDueDate)
    );
  })();

  const onExtend = async () => {
    if (!newDueDate || isDateInvalid || isSubmitting.value) return;

    isSubmitting.on();
    try {
      const result = await extendComplianceFilings({
        complianceItemIds,
        newDueDate,
        reasonForExtension: note.trim() || undefined,
      });

      if (!result.success) {
        setNotification({
          show: true,
          valid: false,
          title: 'Couldn’t extend filings',
          message:
            result.error ??
            'Some of the selected filings can’t be extended. Deselect them and try again.',
        });
        return;
      }

      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: `Extended ${result.data?.extendedCount ?? selectedCount} ${
          (result.data?.extendedCount ?? selectedCount) === 1
            ? 'filing'
            : 'filings'
        }.`,
      });

      onClose();
      await onSuccess?.();
    } finally {
      isSubmitting.off();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting.value) onClose();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start border-b-0 pb-0">
          <DialogTitle>Extend filing due date</DialogTitle>
          <DialogDescription>
            Update the deadline for{' '}
            <span className="font-bold">
              {selectedCount} {selectedCount === 1 ? 'filing' : 'filings'}
            </span>
            . The same due date is applied to every selected filing.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-xl p-3xl">
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Extended due date</span>
              <span className="text-utility-violet-600">*</span>
            </Label>
            <DateField
              value={newDueDate}
              onChange={setNewDueDate}
              displayFormat="in"
              fullWidth
              error={
                Boolean(newDueDate) && isDateInvalid
                  ? 'New due date must be after the current due date of every selected filing.'
                  : undefined
              }
            />
          </FieldWrapper>
          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Note</span>
              <span className="text-text-quaternary">(optional)</span>
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Type here..."
              maxLength={2000}
            />
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button
            variant="secondary"
            onClick={onClose}
            size="md"
            disabled={isSubmitting.value}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onExtend}
            size="md"
            isLoading={isSubmitting.value}
            disabled={!newDueDate || isDateInvalid || isSubmitting.value}
          >
            Extend due date
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkExtendFilingsModal;
